import {
	AfterViewChecked,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	model,
	viewChildren,
} from "@angular/core";
import { NgClass, DatePipe, NgStyle, CommonModule, NgTemplateOutlet } from "@angular/common";
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from "@angular/cdk/scrolling";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { faCopy, faEnvelope } from "@fortawesome/pro-light-svg-icons";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { MatTabsModule } from "@angular/material/tabs";

import {
	MessageModel,
	MessageTypes,
	SendersTypes,
	MessageScopes,
} from "libs/messages/src/lib/models/message.model";
import { MessageTypeIcons } from "libs/messages/src/lib/services/message-type-icons.lib.service";
import { getSendersTypeLabel, openImageInNewTab } from "libs/messages/src/lib/utils/message-format.utils";
import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";

@Component({
	selector: "lib-message-list",
	templateUrl: "./message-list.component.html",
	styleUrls: ["./message-list.component.scss"],
	imports: [
		CdkFixedSizeVirtualScroll,
		CdkVirtualForOf,
		CdkVirtualScrollViewport,
		CommonModule,
		DatePipe,
		FaIconComponent,
		MatTabsModule,
		NgClass,
		NgStyle,
		NgTemplateOutlet,
		TranslatePipe,
	],
})
export class MessageListComponent implements AfterViewChecked {
	private readonly translate = inject(TranslateService);
	protected readonly config = inject<AppConfig>(APP_CONFIG_TOKEN);

	readonly messages = input.required<MessageModel[]>();
	readonly viewportHeight = input<number>(92);
	readonly unreadPublicMessageIds = input<string[]>([]);
	readonly associatedMessagesMap = input<Map<string, MessageModel[]>>(new Map());
	readonly selectedTabIndex = model(0);

	private readonly messagesContainers = viewChildren<ElementRef<HTMLElement>>("messagesContainer");

	protected readonly faCopy = faCopy;
	protected readonly faEnvelope = faEnvelope;
	protected readonly MessageTypeIcons = MessageTypeIcons;
	protected readonly MessageTypes = MessageTypes;
	protected readonly SendersTypes = SendersTypes;
	protected readonly MessageScopes = MessageScopes;

	protected readonly associatedTreatmentEntries = computed(() => {
		const entries: { treatmentId: string; messages: MessageModel[] }[] = [];
		for (const [treatmentId, messages] of this.associatedMessagesMap().entries()) {
			if (messages.length > 0) {
				entries.push({ treatmentId, messages });
			}
		}
		return entries;
	});

	private imageCountPerContainer = new Map<HTMLElement, number>();

	constructor() {
		effect(() => {
			this.messages();
			this.associatedMessagesMap();
			this.imageCountPerContainer.clear();
			this.selectedTabIndex.set(0);
		});
	}

	ngAfterViewChecked() {
		this.updateImageClickHandlers();
	}

	protected isMessageUnreadByCustomer(message: MessageModel): boolean {
		return this.unreadPublicMessageIds()?.includes(message._id);
	}

	private updateImageClickHandlers(): void {
		for (const container of this.messagesContainers()) {
			const el = container.nativeElement;
			const imageElements = Array.from(el.querySelectorAll("img"));
			if (!imageElements.length || imageElements.length === this.imageCountPerContainer.get(el)) {
				continue;
			}
			imageElements.forEach((img) => {
				img.title = this.translate.instant("MessageImageOpenInNewTabTooltip");
				img.style.cursor = "pointer";
				img.addEventListener("click", (event: Event) => {
					openImageInNewTab((event.target as HTMLImageElement).src);
				});
			});
			this.imageCountPerContainer.set(el, imageElements.length);
		}
	}

	protected getSendersTypeLabel = getSendersTypeLabel;
}
